import React from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NS, PROFILE_KEYS } from '../../../i18n/constants/keys';
import { ComingSoonOverlay } from '../../../ui/mui';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { ProfileHeader } from './ProfileHeader';
import { PersonalInfoForm } from './PersonalInfoForm';

interface IPersonalSectionProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bio: string;
  jobTitle: string;
  profileLink: string;
  profilePicStatus: string;
  userInitials: string;
  isEditing: boolean;
  loading: boolean;
  isEmployee: boolean;
  jobTitleOptions: SelectOptions[];
  selectedJobTitle: SelectOptions | null;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onBioChange: (value: string) => void;
  onJobTitleChange: (option: SelectOptions | null) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onOpenPictureModal: () => void;
}

export const PersonalSection = (props: IPersonalSectionProps) => {
  const {
    onStartEdit,
    onSave,
    onCancel,
    onOpenPictureModal,
    ...formProps
  } = props;

  return (
    <Box>
      <ProfileHeader
        firstName={props.firstName}
        lastName={props.lastName}
        email={props.email}
        jobTitle={props.jobTitle}
        profileLink={props.profileLink}
        profilePicStatus={props.profilePicStatus}
        userInitials={props.userInitials}
        isEditing={props.isEditing}
        loading={props.loading}
        onStartEdit={onStartEdit}
        onSave={onSave}
        onCancel={onCancel}
        onOpenPictureModal={onOpenPictureModal}
      />
      <PersonalInfoForm
        firstName={formProps.firstName}
        lastName={formProps.lastName}
        email={formProps.email}
        phone={formProps.phone}
        bio={formProps.bio}
        isEditing={formProps.isEditing}
        isEmployee={formProps.isEmployee}
        jobTitleOptions={formProps.jobTitleOptions}
        selectedJobTitle={formProps.selectedJobTitle}
        onFirstNameChange={formProps.onFirstNameChange}
        onLastNameChange={formProps.onLastNameChange}
        onPhoneChange={formProps.onPhoneChange}
        onBioChange={formProps.onBioChange}
        onJobTitleChange={formProps.onJobTitleChange}
      />
    </Box>
  );
};

export const AccountPreferencesLocked = () => {
  const { t } = useTranslation(NS.profile);

  return (
    <ComingSoonOverlay label={t(PROFILE_KEYS.actions.comingSoon)}>
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: 'grey.100', borderRadius: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t(PROFILE_KEYS.account.preferences)}
        </Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {t(PROFILE_KEYS.account.language)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(PROFILE_KEYS.account.languageDesc)}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 200 }} tabIndex={-1}>
              <InputLabel>{t(PROFILE_KEYS.account.language)}</InputLabel>
              <Select label={t(PROFILE_KEYS.account.language)} defaultValue="en-US" disabled>
                <MenuItem value="en-US">English (US)</MenuItem>
                <MenuItem value="en-UK">English (UK)</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="fr">French</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {t(PROFILE_KEYS.account.timeZone)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(PROFILE_KEYS.account.timeZoneDesc)}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 200 }} tabIndex={-1}>
              <InputLabel>{t(PROFILE_KEYS.account.timeZone)}</InputLabel>
              <Select label={t(PROFILE_KEYS.account.timeZone)} defaultValue="utc-8" disabled>
                <MenuItem value="utc-8">UTC-8 (Pacific Time)</MenuItem>
                <MenuItem value="utc-5">UTC-5 (Eastern Time)</MenuItem>
                <MenuItem value="utc-0">UTC+0 (GMT)</MenuItem>
                <MenuItem value="utc+1">UTC+1 (Central European Time)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Box>
    </ComingSoonOverlay>
  );
};

interface IPasswordRowProps {
  onChangePassword: () => void;
}

export const PasswordRow = ({ onChangePassword }: IPasswordRowProps) => {
  const { t } = useTranslation(NS.profile);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
    >
      <Box>
        <Typography variant="body1" fontWeight={600}>
          {t(PROFILE_KEYS.account.password)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(PROFILE_KEYS.account.passwordLastUpdated)}
        </Typography>
      </Box>
      <Button variant="contained" onClick={onChangePassword} sx={{ width: { xs: '100%', sm: 'auto' } }}>
        {t(PROFILE_KEYS.actions.changePassword)}
      </Button>
    </Stack>
  );
};
